import { useEffect, useId, useRef, useState } from "react";
import { Crosshair, LoaderCircle, MapPin, Search } from "lucide-react";
import { api } from "./lib/api";
import {
  isLocationInput,
  normalizedLocationInput,
} from "./lib/search";
import type { LatLng, Place } from "./lib/types";
import "./OriginPicker.css";

interface Props {
  center: LatLng;
  currentLabel: string;
  busy: boolean;
  error?: string;
  onCurrent: () => void;
  onSelect: (place: Place) => void;
  onMap: () => void;
}

export default function OriginPicker({
  center,
  currentLabel,
  busy,
  error,
  onCurrent,
  onSelect,
  onMap,
}: Props) {
  const inputId = useId();
  const hintId = useId();
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<Place[]>([]);
  const [searching, setSearching] = useState(false);
  const [searched, setSearched] = useState(false);
  const [searchError, setSearchError] = useState("");
  const [retry, setRetry] = useState(0);
  // GPS changes should update the bias for the next search without repeatedly
  // interrupting a search that the user is already waiting for.
  const centerRef = useRef(center);
  centerRef.current = center;

  useEffect(() => {
    const controller = new AbortController();
    const value = query.trim();
    setResults([]);
    setSearched(false);
    setSearchError("");
    setSearching(value.length >= 2);
    if (value.length < 2) return () => controller.abort();
    const timer = setTimeout(() => {
      const pending = isLocationInput(value)
        ? api
            .resolveLocation(normalizedLocationInput(value), controller.signal)
            .then((place) => [place])
        : api.search(value, centerRef.current, controller.signal);
      void pending
        .then((places) => {
          if (controller.signal.aborted) return;
          setResults(places);
          setSearched(true);
        })
        .catch((cause: unknown) => {
          if (controller.signal.aborted) return;
          setSearchError(
            cause instanceof Error
              ? cause.message
              : "לא הצלחנו לחפש כרגע. נסו שוב.",
          );
        })
        .finally(() => {
          if (!controller.signal.aborted) setSearching(false);
        });
    }, 450);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [query, retry]);

  return (
    <section className="origin-picker" aria-label="בחירת נקודת מוצא" dir="rtl">
      <p className="origin-picker-current">המוצא שנבחר: {currentLabel}</p>
      <div className="origin-picker-actions">
        <button
          type="button"
          className="origin-picker-current-button"
          onClick={onCurrent}
          disabled={busy}
          aria-busy={busy}
        >
          {busy ? (
            <LoaderCircle size={20} className="spin" aria-hidden="true" />
          ) : (
            <Crosshair size={20} aria-hidden="true" />
          )}
          {busy ? "מאתרים את המיקום…" : "המיקום שלי עכשיו"}
        </button>
        <button type="button" onClick={onMap}>
          <MapPin size={20} aria-hidden="true" /> בחירה במפה
        </button>
      </div>
      {error && <p className="origin-picker-error" role="alert">{error}</p>}
      <label htmlFor={inputId}>חיפוש נקודת מוצא</label>
      <div className="origin-picker-input">
        <Search size={19} aria-hidden="true" />
        <input
          id={inputId}
          type="search"
          value={query}
          maxLength={200}
          placeholder="רחוב, מספר בית ועיר"
          autoComplete="off"
          enterKeyHint="search"
          aria-describedby={hintId}
          onChange={(event) => setQuery(event.target.value)}
        />
      </div>
      <p id={hintId} className="origin-picker-hint">
        אפשר לתכנן נסיעה מכל כתובת, גם בלי להיות שם.
      </p>
      <div className="origin-picker-results" aria-busy={searching}>
        {searching && (
          <p className="origin-picker-status" role="status">
            <LoaderCircle size={17} className="spin" aria-hidden="true" /> מחפשים…
          </p>
        )}
        {searchError && (
          <div className="origin-picker-error" role="alert">
            <p>{searchError}</p>
            <button type="button" onClick={() => setRetry((value) => value + 1)}>
              ניסיון נוסף
            </button>
          </div>
        )}
        {searched && !searching && results.length === 0 && (
          <p className="origin-picker-status" role="status">
            לא נמצאו תוצאות. נסו להוסיף שם עיר או לבחור במפה.
          </p>
        )}
        {results.length > 0 && (
          <ul aria-label="תוצאות לחיפוש המוצא">
            {results.map((place) => (
              <li key={place.id}>
                <button type="button" onClick={() => onSelect(place)}>
                  <MapPin size={19} aria-hidden="true" />
                  <span>
                    <strong>{place.name}</strong>
                    {place.address && <span>{place.address}</span>}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}
